import React, { useCallback, useMemo } from 'react';
import { Text } from '@televet/kibble-ui/build/components/Text';
import { MenuItemProps } from '@televet/kibble-ui/build/components/Menu/components/MenuItem';
import { Link } from 'react-router-dom';
import {
  AutomationsFieldDescription,
  AutomationsFieldLabel,
  AutomationsFieldContainer,
  AutomationsTagBlock,
  TagDisabledState,
} from 'pages/Automations/components/Layout';
import { useGetAutomationFormTemplateOptionsQuery } from 'shared/types/graphql';
import { useAppDispatch, useAppSelector } from 'state/hooks';
import useClinicUser from 'shared/hooks/useClinicUser';
import { updateAutomationErrors, updateAutomationDraft } from '../../../state/automationsSlice';
import { useGetFieldErrorText } from 'pages/Automations/hooks/useGetFieldErrorText';
import { GraphQLFetchPolicies } from 'shared/enums';
import sortBy from 'lodash-es/sortBy';

export const Forms = (): JSX.Element => {
  const { currentClinicId } = useClinicUser();
  const fieldErrors = useAppSelector((state) => state.automations.errors.forms);
  const errorText = useGetFieldErrorText(fieldErrors);
  const dispatch = useAppDispatch();
  const forms = useAppSelector((state) => state.automations.automationDraft.forms || []);

  const { data: formTemplatesData } = useGetAutomationFormTemplateOptionsQuery({
    variables: {
      where: {
        clinicId: { equals: currentClinicId },
      },
    },
    fetchPolicy: GraphQLFetchPolicies.CacheAndNetwork,
    skip: !currentClinicId,
  });

  const handleRemoveTag = useCallback(
    (tagId: string) => {
      dispatch(updateAutomationDraft({ forms: forms?.filter((formId) => formId !== tagId) }));
    },
    [forms, dispatch],
  );

  const handleAddTag = useCallback(
    (allSelectedItems: MenuItemProps[] | undefined) => {
      if (allSelectedItems) {
        const updates = { forms: allSelectedItems.map(({ value }) => value || '') };
        dispatch(updateAutomationDraft(updates));

        if (Boolean(fieldErrors.length)) {
          dispatch(updateAutomationErrors({ forms: [] }));
        }
      }
    },
    [dispatch, fieldErrors],
  );

  const handleClearAllTags = useCallback(() => {
    dispatch(updateAutomationDraft({ forms: [] }));
  }, [dispatch]);

  const tagBlockFormTemplateOptions = useMemo(() => {
    const options = formTemplatesData?.findManyFormTemplate?.map(({ id, title, isActive, isDeleted }) => ({
      label: title,
      value: id,
      isSelected: Boolean(forms.includes(id)),
      isActive: !!isActive,
      isDeleted: !!isDeleted,
    }));

    return sortBy(options, ['label']) || [];
  }, [formTemplatesData, forms]);

  const selectedFormTemplateTags = useMemo(
    () =>
      tagBlockFormTemplateOptions
        ?.filter(({ isSelected }) => isSelected)
        ?.map(({ label, value, isActive, isDeleted }) => ({
          label: `${label}${
            isDeleted ? ` (${TagDisabledState.Deleted})` : !isActive ? ` (${TagDisabledState.Inactive})` : ''
          }`,
          value: value || '',
        })) || [],
    [tagBlockFormTemplateOptions],
  );

  return (
    <AutomationsFieldContainer>
      <AutomationsFieldLabel>Forms</AutomationsFieldLabel>
      <AutomationsFieldDescription>
        This automation will trigger when the following forms are submitted. Forms can be created and edited on the{' '}
        <Link to="/forms">
          <Text variant="interactive" textDecoration="underline">
            Forms
          </Text>
        </Link>{' '}
        page.
      </AutomationsFieldDescription>
      <AutomationsTagBlock
        tags={selectedFormTemplateTags}
        onRemoveTag={handleRemoveTag}
        onClearAll={handleClearAllTags}
        hasError={Boolean(fieldErrors.length)}
        errorText={errorText}
        listProps={{
          isMultiSelect: true,
          isSearchable: true,
          canSelectAll: true,
          onSelectAll: (selectedItems): void => handleAddTag(selectedItems),
          onSelect: (selectedItem, allSelectedItems): void => handleAddTag(allSelectedItems),
          menuItems: tagBlockFormTemplateOptions.filter(({ isActive, isDeleted }) => !isDeleted && isActive),
          maxH: '300px',
        }}
      />
    </AutomationsFieldContainer>
  );
};
